import { useEffect, useState } from 'react';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import {
	H4,
	Caption,
	Select,
	DateRangePicker,
	DEFAULT_PRESETS,
} from '@components';
import { useGetDifotShipperOptions, useGetLocationGroups } from '@api';
import { TDateRange, TDateRangePreset, TEXT_WEIGHT } from '@types';
import styles from './AnalyticsFilterBar.module.scss';

const LAST_30_DAYS: TDateRangePreset = {
	key: 'last_30_days',
	label: 'Last 30 days',
	resolve: (t) => ({ start: subDays(t, 29), end: t }),
}

const LAST_90_DAYS: TDateRangePreset = {
	key: 'last_90_days',
	label: 'Last 90 days',
	resolve: (t) => ({ start: subDays(t, 89), end: t }),
}

const LAST_12_MONTHS: TDateRangePreset = {
	key: 'last_12_months',
	label: 'Last 12 months',
	resolve: (t) => ({ start: subMonths(t, 12), end: t }),
}

const ANALYTICS_PRESETS: TDateRangePreset[] = [
	...DEFAULT_PRESETS,
	LAST_30_DAYS,
	LAST_90_DAYS,
	LAST_12_MONTHS,
];

export interface IAnalyticsFilterBarProps {
	shippers: number[];
	onShippersChange: (ids: number[]) => void;
	locationGroups: number[];
	onLocationGroupsChange: (ids: number[]) => void;
	dateRange: TDateRange;
	onDateRangeChange: (range: TDateRange) => void;
	/** Overrides the default "Controls all data shown below". */
	caption?: string;
}

type TOption = {
	label: string;
	value: number;
}

/**
 * The filter band that heads every Analytics dashboard tab.
 */
export const AnalyticsFilterBar = (props: IAnalyticsFilterBarProps) => {
	const {
		shippers,
		onShippersChange,
		locationGroups,
		onLocationGroupsChange,
		dateRange,
		onDateRangeChange,
		caption = 'Controls all data shown below',
	} = props;

	const [shipperOptions, setShipperOptions] = useState<TOption[]>([]);
	const [groupOptions, setGroupOptions] = useState<TOption[]>([]);
	const { callGetDifotShipperOpts } = useGetDifotShipperOptions();
	const { callGetLocationGroups } = useGetLocationGroups();

	useEffect(() => {
		callGetDifotShipperOpts().then((res) => {
			if (res?.data?.data) setShipperOptions(res.data.data);
		}).catch((err) => {
			console.error('Failed to load DIFOT shipper options', err);
		});
	}, []);

	useEffect(() => {
		callGetLocationGroups().then((res) => {
			if (res?.data) setGroupOptions(res.data.map((g) => ({ label: g.name, value: g.id })));
		}).catch((err) => {
			console.error('Failed to load location groups', err);
		});
	}, []);

	return (
		<div className={styles.filtersBar} data-testid={'@Ofload-AnalyticsFilterBar-Container'}>
			<div className={styles.filtersLabel}>
				<H4 weight={TEXT_WEIGHT.SEMI_BOLD}>Filters</H4>
				<Caption color={'var(--neutral-06)'}>{caption}</Caption>
			</div>
			<div className={styles.filterControls}>
				<Select
					name={'shipper_filter'}
					multiple
					width={'200px'}
					placeholder={'Select shipper'}
					value={shippers}
					onChange={(ids: number[]) => onShippersChange(ids ?? [])}
				>
					{shipperOptions.map((opt) => (
						<Select.Option key={opt.value} value={opt.value}>
							{opt.label}
						</Select.Option>
					))}
				</Select>
				<Select
					name={'location_group_filter'}
					multiple
					width={'200px'}
					placeholder={'Select location group'}
					value={locationGroups}
					onChange={(ids: number[]) => onLocationGroupsChange(ids ?? [])}
				>
					{groupOptions.map((opt) => (
						<Select.Option key={opt.value} value={opt.value}>
							{opt.label}
						</Select.Option>
					))}
				</Select>
				<DateRangePicker value={dateRange} onChange={onDateRangeChange} presets={ANALYTICS_PRESETS} />
			</div>
		</div>
	);
}
